import api from "./axios";

export interface AuditLogValueChange {
  old: unknown;
  new: unknown;
}

export interface AuditLogEntry {
  action: string;
  user_id: string;
  user_email?: string;
  timestamp: string;
  changes?: Record<string, AuditLogValueChange>;
}

export interface ClusterItem {
  name: string;
  animals: string[];
}

export interface ClusterAnalysis {
  animals: string[];
  total_unique: number;
  total_with_repetitions: number;
  repetitions: string[];
  clusters: ClusterItem[];
  cluster_count: number;
  switches: number;
}

export interface AudioRecording {
  url: string;
  sequence: number;
  uploaded_by: string;
  uploaded_by_email?: string;
  uploaded_at: string;
  transcription?: string | null;
  cluster_analysis?: ClusterAnalysis | null;
  stt_language?: string | null;
}

export type TestPhotoType = "moca" | "qmci" | "depression";

export interface TestPhoto {
  url: string;
  thumb_url?: string | null;
  medium_url?: string | null;
  sequence: number;
  uploaded_by: string;
  uploaded_by_email?: string;
  uploaded_at: string;
  file_name?: string | null;
}

export interface PatientSharedNote {
  note_id: string;
  text: string;
  author_id?: string | null;
  author_email?: string | null;
  author_name?: string | null;
  created_at: string;
}

export interface Patient {
  id: string;
  patient_id: string;
  sex: string | null;
  age: number | null;
  education: string | null;
  language: string | null;
  free_notes: string | null;
  moca_score: number | null;
  qmci_score: number | null;
  depression_score: number | null;
  eye_checks: Record<string, boolean> | null;
  diseases: string[] | null;
  neurologist_audio_animals: AudioRecording[];
  neurologist_audio_story_retell: AudioRecording[];
  test_photos_moca?: TestPhoto[];
  test_photos_qmci?: TestPhoto[];
  test_photos_depression?: TestPhoto[];
  shared_notes?: PatientSharedNote[];
  face_embedding_id: number | null;
  created_by: string | null;
  created_by_user: { id: string; email: string } | null;
  created_at: string | null;
  audit_log?: AuditLogEntry[];
}

export interface PatientListItem extends Patient {
  scan_count: number;
}

export interface PaginatedResponse<T> {
  total: number;
  items: T[];
}

export interface ScanListItem {
  _id: string;
  scan_start: string | null;
  scan_end: string | null;
  sex: string | null;
  age: number | null;
  language: string | null;
  scan_part: string | null;
  display_mode: string | null;
  free_notes: string | null;
  patient_id: string | null;
  user_id: string | null;
  user: { id: string; email: string; name?: string | null; surname?: string | null; role?: string | null } | null;
  created_at: string | null;
  mci_status?: "processing" | "done" | "failed" | null;
  mci_result?: {
    confidence_pct?: number;
    zone?: string;
    thresholds?: { rule_out?: number; rule_in?: number; severe?: number };
  } | null;  // веб-прохождение по одноразовой ссылке
  administration?: "remote" | "supervised" | null;
  attempt_status?: "in_progress" | "completed" | "interrupted" | null;
  test_link_id?: string | null;
}

export interface CreatePatientBody {
  sex: string;
  age: number;
  education?: string;
  language?: string;
  free_notes?: string;
  diseases?: string[];
}

export async function createPatient(data: CreatePatientBody): Promise<Patient> {
  const response = await api.post("/patient", data);
  return response.data;
}

export async function getPatient(patientId: string): Promise<Patient> {
  const response = await api.get(`/patient/${patientId}`);
  return response.data;
}

export async function listPatients(params: {
  skip?: number;
  limit?: number;
  search?: string;
}): Promise<PaginatedResponse<PatientListItem>> {
  const response = await api.get("/patient", { params });
  return response.data;
}

export async function deletePatient(patientId: string): Promise<void> {
  await api.delete(`/patient/${patientId}`);
}

export async function getPatientScans(
  patientId: string,
  params?: { skip?: number; limit?: number }
): Promise<PaginatedResponse<ScanListItem>> {
  const response = await api.get(`/patient/${patientId}/scans`, { params });
  return response.data;
}

export async function updatePatient(
  patientId: string,
  data: { moca_score?: number; qmci_score?: number; depression_score?: number; eye_checks?: Record<string, boolean>; free_notes?: string | null; diseases?: string[] }
): Promise<Patient> {
  const response = await api.patch(`/patient/${patientId}`, data);
  return response.data;
}

export async function uploadNeurologistAudio(
  patientId: string,
  audioType: "animals" | "story_retell",
  audioBlob: Blob,
  sttLanguage?: string
): Promise<{ success: boolean; file_url: string; sequence: number; uploaded_by_email: string; transcription?: string | null; cluster_analysis?: ClusterAnalysis | null }> {
  const formData = new FormData();
  formData.append("audio_type", audioType);
  if (sttLanguage) formData.append("stt_language", sttLanguage);
  formData.append("file", audioBlob, `audio_${audioType}.wav`);
  const response = await api.post(
    `/patient/${patientId}/neurologist-audio`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

export async function updateAudioTranscription(
  patientId: string,
  audioType: "animals" | "story_retell",
  sequence: number,
  transcription: string
): Promise<{ success: boolean; transcription: string; cluster_analysis?: ClusterAnalysis | null }> {
  const response = await api.patch(
    `/patient/${patientId}/neurologist-audio/${audioType}/${sequence}`,
    { transcription }
  );
  return response.data;
}

export async function uploadTestPhoto(
  patientId: string,
  testType: TestPhotoType,
  file: File | Blob,
  fileName?: string
): Promise<{ success: boolean; file_url: string; thumb_url?: string | null; sequence: number; uploaded_by_email: string; test_type: TestPhotoType }> {
  const formData = new FormData();
  formData.append("test_type", testType);
  formData.append("file", file, fileName || `test_photo_${testType}.jpg`);
  const response = await api.post(
    `/patient/${patientId}/test-photos`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

export async function deleteTestPhoto(
  patientId: string,
  testType: TestPhotoType,
  sequence: number
): Promise<void> {
  await api.delete(`/patient/${patientId}/test-photos/${testType}/${sequence}`);
}

export async function addPatientSharedNote(
  patientId: string,
  text: string
): Promise<PatientSharedNote> {
  const response = await api.post(`/patient/${patientId}/notes`, { text });
  return response.data;
}
